/* eslint-disable @typescript-eslint/no-explicit-any */
// Auth monitoring utility for debugging session issues
import { supabase } from './supabase'

// Track session refresh attempts
let refreshAttempts = 0
let lastRefreshTime: Date | null = null

// Monitor session state periodically
export function startAuthMonitoring() {
  // Check session state every 30 seconds
  const monitorInterval = setInterval(async () => {
    try {
      // Check current session
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        return
      }
      
      if (session) {
        const expiresAt = new Date(session.expires_at! * 1000)
        const now = new Date()
        const timeUntilExpiry = expiresAt.getTime() - now.getTime()
        const minutesUntilExpiry = Math.floor(timeUntilExpiry / 1000 / 60)
        
        // Check if session needs refresh (less than 10 minutes until expiry)
        if (minutesUntilExpiry < 10) {
          // Session expiring soon, should trigger refresh
        }
        
        // Check localStorage consistency
        const storedAuth = localStorage.getItem('dnceclub-auth')
        if (storedAuth) {
          try {
            const parsed = JSON.parse(storedAuth)
            const storedSession = parsed.currentSession
            
            if (storedSession) {
              const storedExpiry = new Date(storedSession.expires_at * 1000)
              if (storedExpiry.getTime() !== expiresAt.getTime()) {
                // Session expiry mismatch
              }
            }
          } catch {
            // Failed to parse stored auth
          }
        }
      }
    } catch {
      // Unexpected error during monitoring
    }
  }, 30000) // Check every 30 seconds
  
  // Track refresh attempts
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'TOKEN_REFRESHED') {
      refreshAttempts++
      lastRefreshTime = new Date()
    }
  })
  
  // Return cleanup function
  return () => {
    clearInterval(monitorInterval)
  }
}

// Check for session issues on demand
export async function checkSessionHealth() {
  const issues: string[] = []
  
  try {
    // Check Supabase session
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      issues.push(`Failed to get session: ${error.message}`)
    }
    
    // Check localStorage
    const storedAuth = localStorage.getItem('dnceclub-auth')
    if (!storedAuth) {
      issues.push('No auth data in localStorage')
    } else {
      try {
        const parsed = JSON.parse(storedAuth)
        if (!parsed.currentSession) {
          issues.push('No currentSession in stored auth data')
        } else if (session) {
          // Compare sessions
          const storedExpiry = new Date(parsed.currentSession.expires_at * 1000)
          const memoryExpiry = new Date(session.expires_at! * 1000)
          
          if (Math.abs(storedExpiry.getTime() - memoryExpiry.getTime()) > 1000) {
            issues.push(`Session expiry mismatch: Storage=${storedExpiry.toISOString()}, Memory=${memoryExpiry.toISOString()}`)
          }
        }
      } catch (e) {
        issues.push(`Failed to parse stored auth: ${e}`)
      }
    }
    
    // Check profile cache
    const profileCache = localStorage.getItem('dnceclub_profile_cache')
    if (profileCache) {
      try {
        const parsed = JSON.parse(profileCache)
        const age = Date.now() - parsed.timestamp
        const ageInMinutes = Math.floor(age / 1000 / 60)
        
        if (ageInMinutes > 5) {
          issues.push(`Profile cache is stale (${ageInMinutes} minutes old)`)
        }
      } catch (e) {
        issues.push(`Failed to parse profile cache: ${e}`)
      }
    }
    
    return {
      healthy: issues.length === 0,
      issues,
      session: !!session,
      refreshAttempts,
      lastRefreshTime: lastRefreshTime?.toISOString() || null
    }
  } catch (e) {
    return {
      healthy: false,
      issues: [`Health check failed: ${e}`],
      session: false,
      refreshAttempts,
      lastRefreshTime: lastRefreshTime?.toISOString() || null
    }
  }
}

// Export for use in browser console
if (typeof window !== 'undefined') {
  const w = window as any;
  w.checkSessionHealth = checkSessionHealth;
  w.startAuthMonitoring = startAuthMonitoring;
}